import { Loader2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface DocumentUploadZoneProps {
  onUpload: (file: File) => void;
  isUploading?: boolean;
}

export function DocumentUploadZone({ onUpload, isUploading = false }: DocumentUploadZoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const handleFileSelected = (file: File) => {
    setPendingFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelected(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelected(file);
    // Reset so the same file can be re-selected if needed
    e.target.value = '';
  };

  const handleConfirmUpload = () => {
    if (!pendingFile) return;
    onUpload(pendingFile);
    setPendingFile(null);
  };

  const handleCancel = () => {
    setPendingFile(null);
  };

  return (
    <div className="space-y-2">
      {/* Drop zone */}
      <div
        role="button"
        tabIndex={0}
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
        }}
        aria-label="Document upload area"
        className={cn(
          'flex items-center justify-between rounded-md border-2 border-dashed px-4 py-3 transition-colors cursor-pointer',
          isDragOver
            ? 'border-primary bg-primary/5'
            : 'border-amber-400/70 bg-amber-50/40 hover:border-amber-500 hover:bg-amber-50/60 dark:bg-amber-950/10 dark:hover:bg-amber-950/20',
        )}
        onClick={() => !pendingFile && fileInputRef.current?.click()}
      >
        <div className="flex items-center gap-2">
          <Upload className="h-4 w-4 text-amber-500 flex-shrink-0" />
          <span className="text-sm text-muted-foreground">
            {pendingFile ? (
              <span className="font-medium text-foreground">{pendingFile.name}</span>
            ) : (
              'Drag and drop a file here to upload'
            )}
          </span>
        </div>
        {!pendingFile && (
          <Button
            type="button"
            size="sm"
            className="flex-shrink-0 bg-[#007B7F] hover:bg-[#006669] text-white"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            Select File
          </Button>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleInputChange}
        aria-label="File input"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt,.csv"
      />

      {/* Confirm / cancel — shown after file is chosen */}
      {pendingFile && (
        <div className="rounded-md border bg-muted/30 px-3 py-2.5 flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground truncate">
            Ready to upload: <span className="font-medium text-foreground">{pendingFile.name}</span>
          </span>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={handleCancel} disabled={isUploading}>
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-[#007B7F] hover:bg-[#006669] text-white"
              onClick={handleConfirmUpload}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Uploading…
                </>
              ) : (
                'Upload'
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
